import { useMemo, useState, type ReactNode } from "react";

import { ADS_MANAGER_SURFACE_CLASS } from "../../constants/layout";
import { COPY } from "../../copy";
import { getMonthCohortRows, monthLabelFromIndex } from "../../customersData";
import {
  getCohortAdditionalOrders,
  getCumulativeSalesSeries,
  getMonthDetailKpis,
  getMonthDetailKpiTooltips,
  getMonthDetailLabel,
  MONTH_DETAIL_KPI_TOOLTIPS,
} from "../../monthDetailData";
import {
  formatOverviewCount,
  formatOverviewCurrency,
} from "../../p8OverviewData";
import { fillCopy, renderCopy } from "../../renderCopy";
import { AdsManagerContent } from "../AdsManagerContent";
import { useForecastModal } from "../ForecastExplainer";
import { DottedLabel } from "../overview/DottedLabel";
import { HeaderTooltip } from "../overview/HeaderTooltip";

import { CohortConversionsTable } from "./CohortConversionsTable";
import { FirstYearSalesAndSpendChart } from "./FirstYearSalesAndSpendChart";

const V2_COPY = COPY.cohort.v2;
const IMMEDIATE_TITLE = "Immediate ad sales";

type MonthDetailPageV2Props = {
  monthIndex: number;
  onBack: () => void;
};

function BackChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M10 3L5 8L10 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DeltaTag({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center rounded bg-[#eef1ff] px-1.5 py-0.5 text-xs leading-4 font-medium text-[#3b3f8f]">
      +{pct}%
    </span>
  );
}

/**
 * The lift tag on Continued ad sales. Its sentence counts orders, so the tag
 * goes bare when the cohort has no additional-order count to name: a
 * percentage no reader can unpack is a smaller cost than a sentence that says
 * zero orders produced it.
 */
function ContinuedLiftTag({
  pct,
  additionalOrders,
  month,
}: {
  pct: number;
  additionalOrders: number | null;
  /** Short form, matching the other tooltips rather than the page heading. */
  month: string;
}) {
  if (additionalOrders == null || additionalOrders <= 0) {
    return <DeltaTag pct={pct} />;
  }

  return (
    <HeaderTooltip
      tooltip={renderCopy(
        V2_COPY.kpi.continuedLiftTag,
        {
          count: formatOverviewCount(additionalOrders),
          orders: additionalOrders === 1 ? "order" : "orders",
          month,
          pct,
        },
        "font-semibold"
      )}
    >
      <DeltaTag pct={pct} />
    </HeaderTooltip>
  );
}

/**
 * One cell of the 2x2 metrics grid. The footer slot takes nodes rather than
 * strings because the first-year metric puts an explainer link where the other
 * three put plain supporting figures.
 */
function MetricCell({
  title,
  tooltip,
  value,
  tag,
  footer,
}: {
  title: string;
  tooltip: string;
  value: string;
  tag?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="text-sm leading-5">
      <HeaderTooltip tooltip={tooltip}>
        <DottedLabel>{title}</DottedLabel>
      </HeaderTooltip>
      <p className="mt-1 flex items-center gap-2 text-[30px] leading-[38px] font-normal text-[#333]">
        {value}
        {tag}
      </p>
      {footer ? <div className="mt-2 space-y-1">{footer}</div> : null}
    </div>
  );
}

function FooterLine({ children }: { children: ReactNode }) {
  return <p className="text-xs leading-4 text-[#757575]">{children}</p>;
}

export function MonthDetailPageV2({
  monthIndex,
  onBack,
}: MonthDetailPageV2Props) {
  const openForecastModal = useForecastModal();
  const monthLabel = getMonthDetailLabel(monthIndex);
  // The heading spells the month out; the tooltips abbreviate it so they read
  // the same as the chart axes beside them.
  const monthShort = monthLabelFromIndex(monthIndex);
  const tooltips = getMonthDetailKpiTooltips(monthIndex);
  const series = useMemo(
    () => getCumulativeSalesSeries(monthIndex),
    [monthIndex]
  );
  const cohortRows = useMemo(
    () => getMonthCohortRows(monthIndex),
    [monthIndex]
  );

  /**
   * Every headline figure is read off the same series the chart plots, so the
   * metrics can't drift from the bands beside them. The one exception is the
   * ad-type split under Immediate ad sales, which the series doesn't carry — it
   * comes from the shared KPI builder v1 uses, so both versions report the same
   * breakdown.
   *
   * The additional-order count differences the same series, but through the
   * shared helper: the Overview's month table names that figure too, and the
   * two have to be one number rather than two derivations of it.
   */
  const summary = useMemo(() => {
    const immediate = series[0]?.firstOrderSales ?? 0;
    const realized = series.filter((point) => point.salesToDate != null);
    const latest = realized[realized.length - 1];
    const total = latest?.salesToDate ?? immediate;
    const continued = Math.max(0, total - immediate);

    // Absent once the cohort has passed 12 months, at which point the first-year
    // figure is simply the realized total and stops being a forecast.
    const marker = series.find(
      (point) => point.projectedFirstYearSales != null
    )?.projectedFirstYearSales;
    const forecasted = marker != null && marker > total;

    return {
      immediate,
      continued,
      total,
      firstYear: marker ?? total,
      forecasted,
      additionalOrders: getCohortAdditionalOrders(monthIndex),
      continuedPct:
        immediate > 0 ? Math.round((continued / immediate) * 100) : 0,
    };
  }, [series, monthIndex]);

  const immediateFooters = useMemo(
    () =>
      getMonthDetailKpis(monthIndex).find(
        (kpi) => kpi.title === IMMEDIATE_TITLE
      )?.footers ?? [],
    [monthIndex]
  );

  return (
    <div
      className={`min-h-0 flex-1 overflow-y-auto ${ADS_MANAGER_SURFACE_CLASS}`}
    >
      <AdsManagerContent className="pt-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-[#757575] hover:text-[#333]"
        >
          <BackChevronIcon />
          Overview
        </button>
        <h1 className="mt-3 text-[30px] leading-[38px] font-semibold text-[#333]">
          {V2_COPY.heading}
        </h1>
      </AdsManagerContent>
      <div className="mt-4 border-b border-[#dfe0e1]" />
      <AdsManagerContent className="pt-6 pb-32">
        <div className="flex flex-col gap-6">
          <div>
            <h2 className="text-xl leading-8 font-medium text-[#333]">
              {monthLabel}
            </h2>
            <p className="text-sm leading-5 text-[#757575]">
              {V2_COPY.subhead}
            </p>
          </div>

          {/* Metrics and chart share a row: the four figures are the totals the
              three plotted bands add up to, so they're read together. They stack
              below 1100px, where two half-width cards stop fitting the numbers. */}
          <div className="grid gap-6 min-[1100px]:grid-cols-2">
            <div className="bg-fs-surface-primary rounded-lg border border-[#dfe0e1] p-6">
              <div className="grid grid-cols-1 gap-6 min-[520px]:grid-cols-2">
                <MetricCell
                  title={IMMEDIATE_TITLE}
                  tooltip={tooltips.immediateAdSales}
                  value={formatOverviewCurrency(summary.immediate)}
                  footer={immediateFooters.map((line) => (
                    <FooterLine key={line}>{line}</FooterLine>
                  ))}
                />
                <MetricCell
                  title={MONTH_DETAIL_KPI_TOOLTIPS.continuedTitle}
                  tooltip={tooltips.continuedAdSales}
                  value={formatOverviewCurrency(summary.continued)}
                  tag={
                    summary.continuedPct > 0 ? (
                      <ContinuedLiftTag
                        pct={summary.continuedPct}
                        additionalOrders={summary.additionalOrders}
                        month={monthShort}
                      />
                    ) : null
                  }
                  footer={
                    summary.additionalOrders != null &&
                    summary.additionalOrders > 0 ? (
                      <FooterLine>
                        {renderCopy(V2_COPY.kpi.continuedFooter, {
                          count: formatOverviewCount(summary.additionalOrders),
                          orders:
                            summary.additionalOrders === 1 ? "order" : "orders",
                        })}
                      </FooterLine>
                    ) : null
                  }
                />
                <MetricCell
                  title={V2_COPY.kpi.totalTitle}
                  tooltip={fillCopy(V2_COPY.kpi.totalAdSales, {
                    month: monthShort,
                  })}
                  value={formatOverviewCurrency(summary.total)}
                  footer={
                    <>
                      <FooterLine>
                        {formatOverviewCurrency(summary.immediate)} in immediate
                        ad sales
                      </FooterLine>
                      <FooterLine>
                        {formatOverviewCurrency(summary.continued)} in continued
                        ad sales
                      </FooterLine>
                    </>
                  }
                />
                <MetricCell
                  title={
                    summary.forecasted
                      ? MONTH_DETAIL_KPI_TOOLTIPS.firstYearForecastTitle
                      : MONTH_DETAIL_KPI_TOOLTIPS.firstYearTitle
                  }
                  tooltip={
                    summary.forecasted
                      ? tooltips.firstYearAdSalesForecast
                      : tooltips.firstYearAdSales
                  }
                  value={formatOverviewCurrency(summary.firstYear)}
                  footer={
                    // Only while the figure is still moving. A matured cohort's
                    // first year is settled, so there is no forecast left to
                    // explain and the link would raise a doubt about a final
                    // number rather than answering one.
                    summary.forecasted && openForecastModal ? (
                      <button
                        type="button"
                        className="text-xs leading-4 text-[#333] underline"
                        onClick={openForecastModal}
                      >
                        {COPY.shared.forecast.link}
                      </button>
                    ) : null
                  }
                />
              </div>
            </div>

            <FirstYearSalesAndSpendChart data={series} />
          </div>

          <CohortConversionsTable rows={cohortRows} month={monthShort} />
        </div>
      </AdsManagerContent>
    </div>
  );
}
